import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CMSProjects = () => {
  const { getAuthHeader } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Residential',
    image: '',
    location: '',
    year: new Date().getFullYear().toString(),
    description: ''
  });

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/projects`, {
        headers: getAuthHeader()
      });
      setProjects(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching projects:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(
          `${BACKEND_URL}/api/cms/projects/${editingProject.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/cms/projects`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      setDialogOpen(false);
      setEditingProject(null);
      setFormData({ title: '', category: 'Residential', image: '', location: '', year: new Date().getFullYear().toString(), description: '' });
      fetchProjects();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') { console.error('Error saving project:', error);}
      alert('Failed to save project');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData(project);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/cms/projects/${id}`, {
        headers: getAuthHeader()
      });
      fetchProjects();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') { console.error('Error deleting project:', error);}
      alert('Failed to delete project');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Projects Management</h2>
        <Button
          onClick={() => {
            setEditingProject(null);
            setFormData({ title: '', category: 'Residential', image: '', location: '', year: new Date().getFullYear().toString(), description: '' });
            setDialogOpen(true);
          }}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus size={20} className="mr-2" />
          Add Project
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <Card key={project.id}>
            <div className="h-48 overflow-hidden">
              <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
            </div>
            <CardHeader>
              <CardTitle>{project.title}</CardTitle>
              <p className="text-sm text-gray-600">{project.category} • {project.location} • {project.year}</p>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button onClick={() => handleEdit(project)} variant="outline" size="sm">
                  <Edit size={16} />
                </Button>
                <Button onClick={() => handleDelete(project.id)} variant="outline" size="sm" className="text-red-600">
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Infrastructure</option>
                  <option>Interior</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Year *</label>
                <Input
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Location *</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image URL *</label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">
                {editingProject ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSProjects;
